import type { DualMontagePreference } from "../components/customization/customizationTypes";
import {
  isActiveDockPanelKey,
  normalizeDockPanelOrder,
  type DockPanelKey,
} from "../components/dockPanelLabels";
import {
  getChannelSpacingForRowHeightMode,
  migrateLegacyChannelSpacing,
  type WaveformDisplayMode,
} from "../components/eegViewerDisplayHelpers";
import {
  DEFAULT_DOCK_LAYOUT,
  DEFAULT_DOCK_PANEL_ORDER,
  DEFAULT_DOCK_PANEL_VISIBILITY,
  CLINICAL_DOCK_MAX_WIDTH_PX,
  CLINICAL_DOCK_MIN_WIDTH_PX,
  CLINICAL_DOCK_WIDTH_STORAGE_KEY,
  DOCK_BOTTOM_COLLAPSED_STORAGE_KEY,
  DOCK_RIGHT_COLLAPSED_STORAGE_KEY,
  RIGHT_DOCK_MAX_WIDTH_PX,
  RIGHT_DOCK_MIN_WIDTH_PX,
  readDockCollapsedPreference,
  readDockCollapsedPreferenceOverride,
  readDockDimensionPreferenceOverride,
} from "../hooks/useEEGViewerDockLayout";
import { normalizeHexColor } from "../types/eegViewerTraceTypes";
import type {
  DisplayProfileConfig,
  DisplayProfilePolicyResponse,
} from "../types/eegViewerDisplayProfileTypes";
import {
  clampDisplayProfileNumber,
  snapDisplayProfileNumber,
} from "./displayProfileVisualState";

export const DEFAULT_ALLOW_TRACE_OVERFLOW = true;
export const DEFAULT_REFIT_ON_CHANNEL_CHANGE = false;

export const DEFAULT_DUAL_MONTAGE_PREFERENCE: DualMontagePreference = {
  left_montage_id: null,
  right_montage_id: null,
  sync_cursor: true,
  sync_zoom: true,
  sync_playback: true,
  diff_highlight: false,
};

export type DisplayProfileExtractors = {
  extractChannelColorsFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => Record<string, string>;
  extractTrendProfileIdFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => string;
  extractWaveformThicknessFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => number;
  extractChannelSpacingFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => number;
  extractDisplayModeFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => WaveformDisplayMode;
  extractAllowTraceOverflowFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => boolean;
  extractRefitOnChannelChangeFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => boolean;
  extractLabelFontSizeFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => number;
  extractChannelPositionOffsetsFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => Record<string, number>;
  extractDualMontagePreferenceFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => DualMontagePreference;
  extractDockLayoutFromConfig: (config?: DisplayProfileConfig | null) => {
    bottomHeightPx: number;
    rightWidthPx: number;
    clinicalWidthPx: number;
    bottomCollapsed: boolean;
    rightCollapsed: boolean;
    activeBottomTab: DockPanelKey | null;
  };
  extractDockPanelVisibilityFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => Record<DockPanelKey, boolean>;
  extractDockPanelOrderFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => DockPanelKey[];
  extractHiddenChannelsFromConfig: (
    config?: DisplayProfileConfig | null,
  ) => Set<string>;
};

export function createDisplayProfileConfigExtractors(
  displayProfilePolicy: DisplayProfilePolicyResponse,
): DisplayProfileExtractors {
  const extractChannelColorsFromConfig = (
    config?: DisplayProfileConfig | null,
  ): Record<string, string> => {
    const source = config?.channel_colors;
    if (!source || typeof source !== "object" || Array.isArray(source)) {
      return {};
    }

    const normalized: Record<string, string> = {};
    Object.entries(source).forEach(([channelPair, color]) => {
      const pairKey = channelPair.trim();
      const normalizedColor = normalizeHexColor(color);
      if (!pairKey || !normalizedColor) return;
      normalized[pairKey] = normalizedColor;
    });

    return normalized;
  };

  const extractTrendProfileIdFromConfig = (
    config?: DisplayProfileConfig | null,
  ): string => {
    if (!config) return "";
    const value = config.trend_threshold_profile_id;
    return typeof value === "string" ? value : "";
  };

  const extractWaveformThicknessFromConfig = (
    config?: DisplayProfileConfig | null,
  ): number =>
    clampDisplayProfileNumber(
      config?.waveform_thickness,
      displayProfilePolicy.visual.waveform_thickness.default,
      displayProfilePolicy.visual.waveform_thickness.min,
      displayProfilePolicy.visual.waveform_thickness.max,
    );

  const extractChannelSpacingFromConfig = (
    config?: DisplayProfileConfig | null,
  ): number => {
    const fallback =
      config?.row_height_mode === "compact" ||
      config?.row_height_mode === "standard" ||
      config?.row_height_mode === "tall"
        ? getChannelSpacingForRowHeightMode(config.row_height_mode)
        : displayProfilePolicy.visual.channel_spacing.default;
    const raw =
      typeof config?.channel_spacing === "number"
        ? migrateLegacyChannelSpacing(config.channel_spacing)
        : fallback;
    return snapDisplayProfileNumber(
      raw,
      fallback,
      displayProfilePolicy.visual.channel_spacing.min,
      displayProfilePolicy.visual.channel_spacing.max,
      displayProfilePolicy.visual.channel_spacing.step,
    );
  };

  const extractDisplayModeFromConfig = (
    config?: DisplayProfileConfig | null,
  ): WaveformDisplayMode =>
    config?.display_mode === "butterfly" ? "butterfly" : "stacked";

  const extractAllowTraceOverflowFromConfig = (
    config?: DisplayProfileConfig | null,
  ): boolean =>
    typeof config?.allow_trace_overflow === "boolean"
      ? config.allow_trace_overflow
      : DEFAULT_ALLOW_TRACE_OVERFLOW;

  const extractRefitOnChannelChangeFromConfig = (
    config?: DisplayProfileConfig | null,
  ): boolean =>
    typeof config?.refit_on_channel_change === "boolean"
      ? config.refit_on_channel_change
      : DEFAULT_REFIT_ON_CHANNEL_CHANGE;

  const extractLabelFontSizeFromConfig = (
    config?: DisplayProfileConfig | null,
  ): number =>
    clampDisplayProfileNumber(
      config?.label_font_size,
      displayProfilePolicy.visual.label_font_size.default,
      displayProfilePolicy.visual.label_font_size.min,
      displayProfilePolicy.visual.label_font_size.max,
    );

  const extractChannelPositionOffsetsFromConfig = (
    config?: DisplayProfileConfig | null,
  ): Record<string, number> => {
    const source = config?.channel_position_offsets;
    if (!source || typeof source !== "object" || Array.isArray(source)) {
      return {};
    }
    const normalized: Record<string, number> = {};
    const {
      min,
      max,
      default: fallback,
    } = displayProfilePolicy.visual.channel_position_offset;

    Object.entries(source).forEach(([channelLabel, offset]) => {
      const key = channelLabel.trim();
      if (!key || typeof offset !== "number" || !Number.isFinite(offset)) {
        return;
      }
      const clamped = clampDisplayProfileNumber(offset, fallback, min, max);
      if (Math.abs(clamped) < 0.0001) {
        return;
      }
      normalized[key] = clamped;
    });

    return normalized;
  };

  const extractDualMontagePreferenceFromConfig = (
    config?: DisplayProfileConfig | null,
  ): DualMontagePreference => {
    const source = config?.dual_montage_preference;
    if (!source || typeof source !== "object" || Array.isArray(source)) {
      return DEFAULT_DUAL_MONTAGE_PREFERENCE;
    }

    const value = source as Record<string, unknown>;
    return {
      left_montage_id:
        typeof value.left_montage_id === "string" && value.left_montage_id
          ? value.left_montage_id
          : null,
      right_montage_id:
        typeof value.right_montage_id === "string" && value.right_montage_id
          ? value.right_montage_id
          : null,
      sync_cursor:
        typeof value.sync_cursor === "boolean"
          ? value.sync_cursor
          : DEFAULT_DUAL_MONTAGE_PREFERENCE.sync_cursor,
      sync_zoom:
        typeof value.sync_zoom === "boolean"
          ? value.sync_zoom
          : DEFAULT_DUAL_MONTAGE_PREFERENCE.sync_zoom,
      sync_playback:
        typeof value.sync_playback === "boolean"
          ? value.sync_playback
          : DEFAULT_DUAL_MONTAGE_PREFERENCE.sync_playback,
      diff_highlight:
        typeof value.diff_highlight === "boolean"
          ? value.diff_highlight
          : DEFAULT_DUAL_MONTAGE_PREFERENCE.diff_highlight,
    };
  };

  const extractDockLayoutFromConfig = (
    config?: DisplayProfileConfig | null,
  ): {
    bottomHeightPx: number;
    rightWidthPx: number;
    clinicalWidthPx: number;
    bottomCollapsed: boolean;
    rightCollapsed: boolean;
    activeBottomTab: DockPanelKey | null;
  } => {
    const source = config?.dock_layout;
    if (!source || typeof source !== "object" || Array.isArray(source)) {
      return { ...DEFAULT_DOCK_LAYOUT, activeBottomTab: null };
    }

    const value = source as Record<string, unknown>;
    const readNumber = (
      key: "bottom_height_px" | "right_width_px" | "clinical_width_px",
      fallback: number,
    ) => {
      const candidate = value[key];
      if (typeof candidate !== "number" || !Number.isFinite(candidate)) {
        return fallback;
      }
      return candidate;
    };

    const validPanelKeys = new Set<string>(DEFAULT_DOCK_PANEL_ORDER);
    const activeBottomTab =
      typeof value.active_bottom_tab === "string" &&
      validPanelKeys.has(value.active_bottom_tab)
        ? (value.active_bottom_tab as DockPanelKey)
        : null;
    const storedBottomCollapsed = readDockCollapsedPreference(
      DOCK_BOTTOM_COLLAPSED_STORAGE_KEY,
      DEFAULT_DOCK_LAYOUT.bottomCollapsed,
    );
    const storedRightCollapsed = readDockCollapsedPreference(
      DOCK_RIGHT_COLLAPSED_STORAGE_KEY,
      DEFAULT_DOCK_LAYOUT.rightCollapsed,
    );
    const bottomCollapsedOverride = readDockCollapsedPreferenceOverride(
      DOCK_BOTTOM_COLLAPSED_STORAGE_KEY,
    );
    const rightCollapsedOverride = readDockCollapsedPreferenceOverride(
      DOCK_RIGHT_COLLAPSED_STORAGE_KEY,
    );
    const clinicalWidthOverride = readDockDimensionPreferenceOverride(
      CLINICAL_DOCK_WIDTH_STORAGE_KEY,
    );

    return {
      bottomHeightPx: Math.max(
        160,
        Math.min(
          540,
          readNumber("bottom_height_px", DEFAULT_DOCK_LAYOUT.bottomHeightPx),
        ),
      ),
      rightWidthPx: Math.max(
        RIGHT_DOCK_MIN_WIDTH_PX,
        Math.min(
          RIGHT_DOCK_MAX_WIDTH_PX,
          readNumber("right_width_px", DEFAULT_DOCK_LAYOUT.rightWidthPx),
        ),
      ),
      clinicalWidthPx: Math.max(
        CLINICAL_DOCK_MIN_WIDTH_PX,
        Math.min(
          CLINICAL_DOCK_MAX_WIDTH_PX,
          clinicalWidthOverride ??
            readNumber(
              "clinical_width_px",
              DEFAULT_DOCK_LAYOUT.clinicalWidthPx,
            ),
        ),
      ),
      bottomCollapsed:
        // Always restore from the user's localStorage preference (or default=true).
        // Ignore study-saved bottom_collapsed — the dock should start collapsed
        // on every session unless the user explicitly expanded it this session.
        bottomCollapsedOverride ?? storedBottomCollapsed,
      rightCollapsed:
        rightCollapsedOverride ??
        (typeof value.right_collapsed === "boolean"
          ? value.right_collapsed
          : storedRightCollapsed),
      activeBottomTab,
    };
  };

  const extractDockPanelVisibilityFromConfig = (
    config?: DisplayProfileConfig | null,
  ): Record<DockPanelKey, boolean> => {
    const source = config?.panel_visibility;
    if (!source || typeof source !== "object" || Array.isArray(source)) {
      return DEFAULT_DOCK_PANEL_VISIBILITY;
    }

    const value = source as Record<string, unknown>;
    return {
      ...DEFAULT_DOCK_PANEL_VISIBILITY,
      annotation_timeline: false,
      event_detection:
        typeof value.event_detection === "boolean"
          ? value.event_detection
          : DEFAULT_DOCK_PANEL_VISIBILITY.event_detection,
      trend_correlation:
        typeof value.trend_correlation === "boolean"
          ? value.trend_correlation
          : DEFAULT_DOCK_PANEL_VISIBILITY.trend_correlation,
      spectrogram:
        typeof value.spectrogram === "boolean"
          ? value.spectrogram
          : DEFAULT_DOCK_PANEL_VISIBILITY.spectrogram,
      psd_analysis:
        typeof value.psd_analysis === "boolean"
          ? value.psd_analysis
          : DEFAULT_DOCK_PANEL_VISIBILITY.psd_analysis,
      annotation_list:
        typeof value.annotation_list === "boolean"
          ? value.annotation_list
          : DEFAULT_DOCK_PANEL_VISIBILITY.annotation_list,
    };
  };

  const extractDockPanelOrderFromConfig = (
    config?: DisplayProfileConfig | null,
  ): DockPanelKey[] => {
    const source = config?.panel_order;
    if (!Array.isArray(source)) {
      return DEFAULT_DOCK_PANEL_ORDER;
    }

    const normalized = source.filter(
      (value): value is DockPanelKey =>
        typeof value === "string" && isActiveDockPanelKey(value as DockPanelKey),
    );
    return normalizeDockPanelOrder(normalized);
  };

  // EEG-CUX-006: extract persisted hidden channels from a display profile config
  const extractHiddenChannelsFromConfig = (
    config?: DisplayProfileConfig | null,
  ): Set<string> => {
    if (!Array.isArray(config?.hidden_channels)) return new Set();
    return new Set(
      (config.hidden_channels as unknown[]).filter(
        (v): v is string => typeof v === "string",
      ),
    );
  };

  return {
    extractChannelColorsFromConfig,
    extractTrendProfileIdFromConfig,
    extractWaveformThicknessFromConfig,
    extractChannelSpacingFromConfig,
    extractDisplayModeFromConfig,
    extractAllowTraceOverflowFromConfig,
    extractRefitOnChannelChangeFromConfig,
    extractLabelFontSizeFromConfig,
    extractChannelPositionOffsetsFromConfig,
    extractDualMontagePreferenceFromConfig,
    extractDockLayoutFromConfig,
    extractDockPanelVisibilityFromConfig,
    extractDockPanelOrderFromConfig,
    extractHiddenChannelsFromConfig,
  };
}
